import Dexie, { type Table } from 'dexie';
import { V2_STORES } from './schema/v2';
import { V3_STORES } from './schema/v3';
import { V4_STORES } from './schema/v4';
import { BUILT_IN_MEASUREMENT_TYPES, builtInMeasurementTypeId } from './seed/measurementTypes';
import type {
  BackupRecordRow,
  BodyMeasurementRecord,
  CardioSegmentRecord,
  ExerciseRecord,
  ExerciseUserStateRecord,
  HistoryEditAuditRecord,
  MeasurementTypeRecord,
  PersonalRecordRecord,
  PlanBlockRecord,
  PlanDayRecord,
  PlanDayScheduleRecord,
  PlanExerciseRecord,
  PlanRecord,
  PlanSetRecord,
  ProgressionRuleRecord,
  SessionBlockRecord,
  SessionExerciseRecord,
  SessionRecord,
  SessionSetRecord,
  UserMediaRecord,
} from './types';

/**
 * The LiftX local database.
 *
 * Phase 1 established the mechanism — naming, versioning, migration, and the
 * repository boundary — with only the tables Phase 1 needed. Phase 3 adds the
 * application data model as version 2. Later phases add tables by adding a new
 * version; they never edit a shipped one.
 *
 * Rules that hold for every future version:
 *   1. Version numbers only ever increase.
 *   2. A shipped version's schema is NEVER edited. Changing it would silently
 *      diverge from the store already on the device.
 *   3. Structural change comes with an `.upgrade()` that migrates real data,
 *      and is tested against a store seeded at the previous version — not only
 *      against an empty one, which passes for migrations that destroy data.
 */

export const DATABASE_NAME = 'liftx';

/**
 * Current schema version. Phase 0 §5.5.
 * Phase 1 = 1. Phase 3 = 2. Phase 6 = 3.
 */
export const SCHEMA_VERSION = 4;

/** A persisted application setting. Values are JSON-serialisable. */
export interface SettingRecord {
  /** Primary key, e.g. `appearance.theme`. */
  key: string;
  /**
   * Nullable on purpose: "set, but has no value yet" is a real state and is
   * distinguishable from "absent" (`null` vs `undefined`).
   */
  value: unknown;
  updatedAt: number;
}

/** Provenance of the database itself, for diagnostics and future migrations. */
export interface MetaRecord {
  key: string;
  value: unknown;
}

export class LiftXDatabase extends Dexie {
  settings!: Table<SettingRecord, string>;
  meta!: Table<MetaRecord, string>;

  // ── Added in version 2 (Phase 3) ──────────────────────────────────────
  exercises!: Table<ExerciseRecord, string>;
  userMedia!: Table<UserMediaRecord, string>;
  plans!: Table<PlanRecord, string>;
  planDays!: Table<PlanDayRecord, string>;
  planDaySchedules!: Table<PlanDayScheduleRecord, string>;
  planBlocks!: Table<PlanBlockRecord, string>;
  planExercises!: Table<PlanExerciseRecord, string>;
  planSets!: Table<PlanSetRecord, string>;
  progressionRules!: Table<ProgressionRuleRecord, string>;
  sessions!: Table<SessionRecord, string>;
  sessionBlocks!: Table<SessionBlockRecord, string>;
  sessionExercises!: Table<SessionExerciseRecord, string>;
  sessionSets!: Table<SessionSetRecord, string>;
  cardioSegments!: Table<CardioSegmentRecord, string>;
  personalRecords!: Table<PersonalRecordRecord, string>;
  measurementTypes!: Table<MeasurementTypeRecord, string>;
  bodyMeasurements!: Table<BodyMeasurementRecord, string>;
  backupRecords!: Table<BackupRecordRow, string>;
  historyEditAudit!: Table<HistoryEditAuditRecord, string>;

  // ── Added in version 4 (Multi-user identity architecture, Phase A) ────
  exerciseUserState!: Table<ExerciseUserStateRecord, string>;

  constructor(name: string = DATABASE_NAME) {
    super(name);

    // ── Version 1 (Phase 1) ────────────────────────────────────────────
    // SHIPPED. Never edit — the store on the device was created from this
    // exact definition, and changing it would silently diverge from it.
    // Only indexed properties are listed; Dexie stores the whole object.
    this.version(1).stores({
      settings: 'key',
      meta: 'key',
    });

    // ── Version 2 (Phase 3) ────────────────────────────────────────────
    // Purely additive: `settings` and `meta` are restated unchanged (Dexie
    // treats each version's map as the complete schema, so omitting a store
    // would delete it), and every other store is new.
    //
    // The upgrade function therefore has no rows to transform. It records
    // that the migration ran, which is the difference between "we believe it
    // ran" and evidence on the device.
    //
    // Seeding does NOT happen here. Dexie only runs upgrade functions when
    // stepping a database up from a lower version — a database created fresh
    // at v2 never calls this. Seeding lives in `openDatabase` instead, so
    // fresh installs and upgrades reach the same state.
    this.version(2)
      .stores(V2_STORES)
      .upgrade(async (transaction) => {
        await transaction.table('meta').put({
          key: 'migratedToV2At',
          value: Date.now(),
        });
      });

    // ── Version 3 (Phase 6) ────────────────────────────────────────────
    // Purely an added index — `personalRecords` gains `sessionId`, restated
    // here since Dexie treats each version's map as the complete schema.
    // No `.upgrade()`: adding an index to an existing store requires no data
    // transform — Dexie populates the new index from each row's existing
    // `sessionId` field itself during the version-3 upgrade transaction.
    this.version(3).stores(V3_STORES);

    // ── Version 4 (Multi-user identity architecture, Phase A) ─────────
    // Purely additive: one new, empty-by-default store. No `.upgrade()` —
    // there is nothing to backfill `exerciseUserState` from; it's a
    // sync-layer mirror that `reconcileExerciseUserState()`
    // (`sync/syncEngine.ts`) populates from the *existing* `exercises`
    // table's own overlay fields on its own next run, not something this
    // migration needs to construct.
    this.version(4).stores(V4_STORES);
  }
}

/**
 * The application-wide instance.
 *
 * Tests construct their own `LiftXDatabase` against a unique name instead of
 * importing this, so they never share state with each other or with the app.
 */
export const db = new LiftXDatabase();

/**
 * Ensure the built-in measurement types exist. Idempotent.
 *
 * Runs on **every** open rather than only inside the v2 upgrade, because Dexie
 * does not call upgrade functions for a database created fresh at the current
 * version — a fresh install would otherwise have no measurement types at all,
 * silently, and only on a real device.
 *
 * Matching is by `slug`, not `id`: a row already present under any id (an old
 * random one from before the 2026-08-24 sync fix, or the current deterministic
 * one) counts as "already seeded" — this function never re-adds a slug that's
 * already there. Existing rows are left completely alone — a user who renamed
 * or hid a built-in keeps that change through every subsequent open. The id
 * assigned to a genuinely-missing type is deterministic
 * ({@link builtInMeasurementTypeId}), so this is now idempotent *across
 * devices* too, not just within one — see that function's doc for why.
 */
export async function seedBuiltInMeasurementTypes(database: LiftXDatabase): Promise<number> {
  const existing = await database.measurementTypes.toArray();
  const known = new Set(existing.map((row) => row.slug));

  const missing = BUILT_IN_MEASUREMENT_TYPES.filter((type) => !known.has(type.slug)).map((type) => ({
    ...type,
    id: builtInMeasurementTypeId(type.slug),
  }));

  if (missing.length > 0) await database.measurementTypes.bulkAdd(missing);
  return missing.length;
}

/**
 * Open the database and record its provenance.
 *
 * Returns the resolved schema version so the caller can surface it. Throws if
 * the database cannot be opened at all — the caller is expected to present a
 * recovery path rather than crash (Phase 0 §11.6).
 */
export async function openDatabase(database: LiftXDatabase = db): Promise<number> {
  await database.open();
  await seedBuiltInMeasurementTypes(database);

  const existing = await database.meta.get('createdAt');
  if (existing === undefined) {
    await database.meta.put({ key: 'createdAt', value: Date.now() });
  }
  await database.meta.put({ key: 'schemaVersion', value: SCHEMA_VERSION });

  return SCHEMA_VERSION;
}
